import * as Sentry from '@sentry/react';

import { EngagementNextAction } from '@/__generated__/graphql';

/**
 * `EngagementNextAction` → the line shown under a dashboard row.
 *
 * **The api returns enum keys, never copy.** ENG-5449's "locked verbatim copy
 * consumed from the API" phrasing is stale against the as-built: the strings
 * live here.
 *
 * The keys are **viewer-aware** since ENG-5609 — the same engagement yields a
 * different key to the expert and to the customer side — so this map spans both
 * dashboards and each line is written for whoever can actually receive it.
 * `REVIEW_OFFER` changed hands in that ticket: it used to be the expert's
 * pre-offer key (with the make-your-offer wording now on `MAKE_OFFER`), which
 * is what made ENG-5422's AC-7 a dead CTA.
 *
 * These are DRAFTS. The final wording belongs to the v5 §9.6 copy batch tracked
 * on ENG-5613 (content owner: Brian) — this map is the single swap site.
 *
 * `NONE` maps to null rather than an empty string: a row with nothing pending
 * renders no line at all, never a blank one.
 */
export const NEXT_ACTION_COPY: Record<EngagementNextAction, string | null> = {
  // Expert.
  MAKE_OFFER: 'Review the request and make your offer',
  SUBMIT_HOURS: 'Submit hours for the current period',

  // Customer side.
  REVIEW_OFFER: "Review the expert's offer",
  REVIEW_ADJUSTMENT: 'Governance returned this with feedback — review it',
  APPROVE_GOVERNANCE: 'Your approval is needed',
  CONFIRM_HOURS: 'Confirm submitted hours',

  NONE: null,
};

/**
 * Rows deep-link to the tab where the action is taken.
 *
 * `SUBMIT_HOURS` and `CONFIRM_HOURS` are both the Hours tab — the two sides of
 * the same row. `APPROVE_GOVERNANCE` lands on the Overview too: its banner
 * carries the three decisions beside the terms they turn on (ENG-5752), where
 * the Approvals queue shows one line. Everything else is an ActionStrip
 * affordance on the Overview, which is the default tab and needs no parameter.
 */
export function nextActionHref(engagementId: string, action: EngagementNextAction): string {
  const base = `/engagements/${encodeURIComponent(engagementId)}`;
  switch (action) {
    case EngagementNextAction.SubmitHours:
    case EngagementNextAction.ConfirmHours:
      return `${base}?tab=hours`;
    default:
      return base;
  }
}

/**
 * The map lookup, for render sites that take their key off the wire.
 *
 * A deployed api can hold an enum member this build does not — `NEXT_ACTION_COPY`
 * grew by four in ENG-5609 alone — and a bare `NEXT_ACTION_COPY[key]` returns
 * `undefined` there, so the row silently drops its line with nothing to say
 * which value we could not read. Mirrors `reportUnknownStatus` in
 * `EngagementHub/status.ts`: render the neutral thing, leave a breadcrumb.
 *
 * `nextActionHref` needs no twin — its `default:` sends an unknown key to the
 * Hub, which is a correct destination rather than a silent miss, and the same
 * render already breadcrumbs through here.
 */
export function nextActionCopyFor(action: EngagementNextAction): string | null {
  const copy = NEXT_ACTION_COPY[action];
  if (copy === undefined) {
    Sentry.addBreadcrumb({
      category: 'engagement.nextAction',
      level: 'warning',
      message: 'Unknown engagement next action',
      data: { action },
    });
    return null;
  }
  return copy;
}
